import type {ReactElement} from 'react';
import {useEffect, useMemo, useRef} from 'react';
import type {AiChatWindowPromptBoxProps} from './ai-chat-window-prompt-box.types';
import styles from './ai-chat-window-prompt-box.module.scss';
import {AiPromptBox} from '@Components/ai-prompt-box/ai-prompt-box';
import {AiPromptBoxSize, PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {AiPromptBoxUploads} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-uploads/ai-prompt-box-uploads';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {getDefaultAttachments} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {useProTipContext} from '@Components/ai-prompt-box/hooks/useProTipContext';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {useScrollToBottom} from '@Components/ai-chat-window/hooks/useScrollToBottom';
import type {FooterLeftAction, FooterLeftActionProps} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-left-actions/ai-prompt-box-footer-left-actions.types';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {checkDailyLimit} from '@Libraries/ai-chat.library';
import {useAiChatPromptBoxCoordinator} from '@Components/ai-chat-window/hooks/useAiChatPromptBoxCoordinator';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {useAudioLimit} from '@AiChat/hooks/useAudioLimit';
import {useMicLoginCallback} from '@Components/ai-prompt-box/hooks/useMicLoginCallback';
import {ProgressiveBlurOverlay} from '@Components/progressive-blur-overlay/progressive-blur-overlay';
import {AiPromptBoxTemplatesButton} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-templates-button/ai-prompt-box-templates-button';

export function AiChatWindowPromptBox({...props}: AiChatWindowPromptBoxProps): ReactElement {
  const promptBox = useAiPromptBox(props.id);
  const {handleFocus} = useAudioLimit();
  const {state: promptBoxState, actions: aiPromptBoxActions} = promptBox;
  const {state: aiChatState, actions: aiChatActions} = useAiChat();

  useMicLoginCallback(props.id, aiChatActions.onLoginSuccessActions);

  const {showScrollButton, scrollToBottom} = useScrollToBottom({
    containerId: 'pmw-chat-messages-container',
  });
  const isMobile = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.MOBILE_MODAL_ACTIVATION_BREAKPONT);
  const isTablet = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.LARGE);

  const shouldShowScrollToBottomButton = (): boolean => {
    return props.shouldShowScrollToBottomButton && showScrollButton;
  };

  const shouldShowProgressiveBlur = (): boolean => {
    return Boolean(props.showProgressiveBlur && props.chatId);
  };

  const coordinator = useAiChatPromptBoxCoordinator({
    promptBoxId: props.id,
    chatId: props.chatId,
    isMessageInProgress: props.isMessageInProgress,
  });

  useEffect(() => {
    aiChatActions.registerPromptBoxCoordinator(coordinator);

    return (): void => {
      aiChatActions.registerPromptBoxCoordinator(null);
    };
  }, [aiChatActions.registerPromptBoxCoordinator, coordinator]);

  const createdChatIdsRef = useRef<Set<string>>(new Set());
  const prevChatIdRef = useRef<string | undefined>(props.chatId);

  useEffect(() => {
    if (props.chatId && !prevChatIdRef.current) {
      createdChatIdsRef.current.add(props.chatId);
    }
    prevChatIdRef.current = props.chatId;
  }, [props.chatId]);

  const isNewChat = props.chatId ? createdChatIdsRef.current.has(props.chatId) : true;

  const proTipContext = useProTipContext({
    messages: props.messages,
    chatId: props.chatId,
    attachmentItems: promptBoxState.attachments?.items ?? [],
    isNewChat,
    hasLoadingAttachments: (promptBoxState.attachmentLoaders?.length ?? 0) > 0,
    isMessageInProgress: props.isMessageInProgress,
  });

  useEffect(() => {
    void checkDailyLimit(aiPromptBoxActions.update);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      return;
    }

    aiPromptBoxActions.update({
      size: AiPromptBoxSize.SMALL,
      placeholder: window.i18next.t('pmwjs_describe_the_design_you_want_to_create_elipses'),
    });
  }, [isMobile]);

  const templateSearchTerm = props.chatId ? (aiChatState.getTemplateSearchTerm(props.chatId) ?? undefined) : undefined;

  const promptBoxOptions = useMemo<FooterLeftAction[]>(() => {
    return [
      (actionProps: FooterLeftActionProps): ReactElement | null => <AiPromptBoxUploads {...actionProps} renderAttachmentTooltip={!isTablet} />,
      (actionProps: FooterLeftActionProps): ReactElement | null => <AiPromptBoxTemplatesButton {...actionProps} searchTerms={templateSearchTerm} />,
    ];
  }, [isTablet, templateSearchTerm]);

  const getProgressBlurBackdrop = (): ReactElement => {
    return <ProgressiveBlurOverlay className={styles.progressiveBlurOverlay} horizontalFade />;
  };

  const getScrollToBottomButton = (): ReactElement => {
    return (
      <Icon
        className={`${styles.scrollToBottomIcon} ${!shouldShowScrollToBottomButton() ? styles.hiddenScrollButton : ''}`}
        icon={'icon-arrow-bottom'}
        type={IconType.SHADOW}
        size={IconSize.SIZE_ICON_16}
        shape={IconShape.CIRCLE}
        onClick={scrollToBottom}
      />
    );
  };

  const getPromptBoxPlaceholderText = (): string => {
    if (props.hasChatHistory) {
      return window.i18next.t('pmwjs_make_chaneges_or_create_something_new_ellipses');
    }

    return window.i18next.t('pmwjs_describe_the_design_you_want_to_create_elipses');
  };

  const renderPromptBox = (): ReactElement => {
    return (
      <div className={`flex-v-row ${props.isReturningUser && !props.chatId ? '' : 'spacing-m-l-4 spacing-m-r-4'} ${styles.floatingPromptBox} ${props.chatId ? styles.chatHasMessages : ''}`} style={{position: 'relative'}}>
        {shouldShowProgressiveBlur() ? getProgressBlurBackdrop() : null}
        {getScrollToBottomButton()}
        <AiPromptBox
          id={props.id}
          initialConfig={{
            prompt: props.initialPrompt ?? '',
            size: AiPromptBoxSize.MEDIUM,
            promptBoxState: PromptBoxState.IDLE,
            placeholder: getPromptBoxPlaceholderText(),
            attachments: props.initialAttachments ? {...getDefaultAttachments(), items: props.initialAttachments} : getDefaultAttachments(),
            showDisclaimer: false,
            transcription: {showMic: true},
          }}
          onGenerate={async () => {
            await props.onGenerate(promptBoxState.prompt, promptBoxState.attachments);
          }}
          onRegenerate={(): Promise<void> => {
            props.onRegenerate();
            return Promise.resolve();
          }}
          onStop={props.onStopGeneration}
          containerClasses={`radius-12 _full-width ${styles.promptBox} ${props.chatId ? styles.hasMessages : ''} ${props.containerClassname ?? ''}`}
          options={promptBoxOptions}
          showProtips
          proTipContext={proTipContext}
          focus
          disableAutoActivate
          onFocus={() => {
            handleFocus();
            void checkDailyLimit(aiPromptBoxActions.update);
          }}
        />
      </div>
    );
  };

  return renderPromptBox();
}
